import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import logo from "../../Images/Rocket_on_dark_background_small.webp";

import { fetchUserSession } from "../../redux/slices/userSessionSlice";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { transliterate } from "transliteration";
import { saveJwtToken, saveUserUid } from "../../Utils/authStorage";
import { setCurrentUser } from "../../redux/slices/userSessionSlice";
import { loginMaster } from "../../redux/slices/masterSessionSlice";

const TEST_NUMBERS = {
  "+972541234560": { mode: "login", role: null },
  "+972541234561": { mode: "login", role: "master" },
  "+972541234562": { mode: "register", role: "master" },
  "+972541234563": { mode: "register", role: "user" },
};

// ✅ Вне компонента — не пересоздаётся при ре-рендере, инпут не теряет фокус
const Wrapper = ({ children }) => (
  <div className="auth-page">
    <div className="auth-container">
      <div className="auth-header">
        <Link to="/" className="auth-logo">
          <div className="logo-header-placeholder">
            <img src={logo} alt="ZmanUp Logo" className="logo-header-img" />
          </div>
          <span className="logo-text">ZmanUp</span>
        </Link>
      </div>
      {children}
    </div>
  </div>
);

const OtpVerification = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { phoneNumber, firstName, lastName, mode, registerRole } = location.state || {};
  const uid = location.state?.uid || uuidv4();

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);

  // ── Экраны: null | "notFound" | "chooseRole" | "registerClient" ──────────
  const [screen, setScreen] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [clientFirstName, setClientFirstName] = useState(firstName || "");
  const [clientLastName, setClientLastName] = useState(lastName || "");

  const baseUrl = getServerUrl();
  if (!baseUrl) throw new Error("[OTP] Server URL is empty");

  const publicConfig = {
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  };

  const lang = (i18n?.language || "ru").slice(0, 2);

  const slugify = (s) =>
    transliterate(String(s || ""))
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/^-+|-+$/g, "");

  // ── OTP input handlers ────────────────────────────────────────────────────
  const handleChange = (index, value) => {
    if (value.length > 1) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0)
      document.getElementById(`otp-${index - 1}`)?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6).split("");
    const newCode = ["", "", "", "", "", ""];
    digits.forEach((d, i) => { newCode[i] = d; });
    setCode(newCode);
    document.getElementById(`otp-${Math.min(digits.length, 5)}`)?.focus();
  };

  // ── Test bypass ───────────────────────────────────────────────────────────
  const isTestNumber = TEST_NUMBERS[phoneNumber];

  const handleTestBypass = () => {
    const otp = code.join("");
    if (otp.length < 6) return;
    const testConfig = TEST_NUMBERS[phoneNumber];
    if (!testConfig) return;
    if (testConfig.mode === "login" && testConfig.role === null) { setScreen("notFound"); return; }
    if (testConfig.mode === "login" && testConfig.role === "master") { navigate(`/${lang}/master/test-master`); return; }
    if (testConfig.mode === "register" && testConfig.role === "master") {
      const namesSlug = firstName || lastName ? slugify(`${firstName} ${lastName}`) : "";
      const phoneSlug = `user-${phoneNumber.replace(/\D/g, "").slice(-7)}`;
      const masterLink = `master/${namesSlug || phoneSlug}`;
      navigate(`/${lang}/${masterLink}/create-master`, {
        state: { phoneNumber, firstName, lastName, uid, registerRole: "master", token: "test-token" },
      });
      return;
    }
    if (testConfig.mode === "register" && testConfig.role === "user") { navigate(`/${lang}/user/myappointments`); return; }
  };

  // ── Выбор роли → переход ──────────────────────────────────────────────────
  const handleRoleConfirm = () => {
    if (!selectedRole) return;
    if (selectedRole === "master") {
      const namesSlug = firstName || lastName ? slugify(`${firstName} ${lastName}`) : "";
      const phoneSlug = `user-${phoneNumber.replace(/\D/g, "").slice(-7)}`;
      const masterLink = `master/${namesSlug || phoneSlug}`;
      navigate(`/${lang}/${masterLink}/create-master`, {
        state: { phoneNumber, uid, firstName, lastName, registerRole: "master", from: "not-found-screen" },
      });
      return;
    }
    if (selectedRole === "user") {
      setScreen("registerClient");
    }
  };

  // ── Регистрация клиента ───────────────────────────────────────────────────
  const handleClientRegister = async () => {
    if (loading) return;
    try {
      setLoading(true);

      let userData = null;

      try {
        const res = await axios.post(
          `${baseUrl}/auth/user-register`,
          { phone: phoneNumber, firstName: clientFirstName, lastName: clientLastName, uid },
          publicConfig
        );
        // ✅ берём user и token прямо из ответа
        userData = res.data?.account || res.data;
        if (res.data?.token) saveJwtToken(res.data.token);
      } catch (regErr) {
        const status = regErr?.response?.status;
        const msg = regErr?.response?.data?.error || "";
        if (status === 400 && msg.toLowerCase().includes("already")) {
          // уже зарегистрирован — пробуем залогинить
          const loginRes = await axios.post(
            `${baseUrl}/auth/user-login`,
            { phone: phoneNumber },
            publicConfig
          );
          userData = loginRes.data?.account || loginRes.data;
          if (loginRes.data?.token) saveJwtToken(loginRes.data.token);
        } else {
          throw regErr;
        }
      }

      // ✅ сохраняем юзера в redux напрямую — не ждём session
      if (userData) dispatch(setCurrentUser(userData));

      navigate(`/${lang}/user/myappointments`);
    } catch (err) {
      console.error("[user-register]", err?.response?.data || err.message);
      alert(t("registration-error") || "Ошибка регистрации");
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP ────────────────────────────────────────────────────────────
  const verifyOtp = async (e) => {
    e.preventDefault();
    const otp = code.join("");
    if (otp.length < 6) return;
    if (isTestNumber) { handleTestBypass(); return; }

    try {
      setLoading(true);
      const res = await axios.post(
        `${baseUrl}/otp/validate-otp`,
        { phoneNumber, otp, uid },
        publicConfig
      );

      if (res.data?.message !== "OTP validation successful") {
        alert(t("invalid-otp") || "Неверный код");
        return;
      }

      const { masterExists, master, userExists, user, uid: serverUid, token } = res.data;

      if (token) saveJwtToken(token);
      if (masterExists && serverUid) saveUserUid(serverUid);

      // ─── ЛОГИН ────────────────────────────────────────────────────────────
      if (mode === "login" || !mode) {
        if (masterExists && master) {
          await dispatch(loginMaster(master.phone)).unwrap();
          const linkKey = (master.masterLink || "").replace(/^master\//, "");
          navigate(`/${lang}/master/${linkKey}/master-dashboard`);
          return;
        }
        if (userExists && user) {
          dispatch(setCurrentUser(user));
          navigate(`/${lang}/user/myappointments`);
          return;
        }
        setScreen("notFound");
        return;
      }

      // ─── РЕГИСТРАЦИЯ ──────────────────────────────────────────────────────
      if (mode === "register") {
        const namesSlug = firstName || lastName ? slugify(`${firstName} ${lastName}`) : "";
        const phoneSlug = `user-${phoneNumber.replace(/\D/g, "").slice(-7)}`;
        const masterLink = `master/${namesSlug || phoneSlug}`;

        if (masterExists && master) { navigate(`/${lang}/${master.masterLink}`); return; }

        if (registerRole === "master") {
          navigate(`/${lang}/${masterLink}/create-master`, {
            state: { phoneNumber, firstName, lastName, uid: serverUid || uid, registerRole, token },
          });
          return;
        }

        if (registerRole === "user") {
          try {
            let userData = null;

            try {
              const regRes = await axios.post(
                `${baseUrl}/auth/user-register`,
                { phone: phoneNumber, firstName, lastName, uid: serverUid || uid },
                publicConfig
              );
              userData = regRes.data?.account || regRes.data;
              if (regRes.data?.token) saveJwtToken(regRes.data.token);
            } catch (regErr) {
              const status = regErr?.response?.status;
              const msg = regErr?.response?.data?.error || "";
              if (status === 400 && msg.toLowerCase().includes("already")) {
                // уже зарегистрирован — логиним
                const loginRes = await axios.post(
                  `${baseUrl}/auth/user-login`,
                  { phone: phoneNumber },
                  publicConfig
                );
                userData = loginRes.data?.account || loginRes.data;
                if (loginRes.data?.token) saveJwtToken(loginRes.data.token);
              } else {
                throw regErr;
              }
            }

            // ✅ сохраняем в redux напрямую
            if (userData) dispatch(setCurrentUser(userData));
            navigate(`/${lang}/user/myappointments`);
          } catch (err) {
            console.error("[user-register error]", err?.response?.data || err.message);
            alert(t("registration-error") || "Ошибка регистрации");
          }
          return;
        }
      }

      alert(t("user_not_found_register") || "Пользователь не найден");
      navigate(`/${lang}/login`);
    } catch (err) {
      console.error("[OTP verify error]", err?.response?.data || err.message);
      alert(t("otp-validation-error") || "Ошибка верификации");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const resendOtp = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${baseUrl}/otp/request-otp`,
        {
          phoneNumber, deliveryMethod: "whatsapp", language: lang === "en" ? "he" : lang
        },
        publicConfig
      );
      setCode(["", "", "", "", "", ""]);
      document.getElementById("otp-0")?.focus();
    } catch (err) {
      console.error("[OTP resend error]", err?.response?.data || err.message);
      alert(t("otp-send-error") || "Ошибка отправки");
    } finally {
      setLoading(false);
    }
  };

  // ── Экран: аккаунт не найден ──────────────────────────────────────────────
  if (screen === "notFound") {
    return (
      <Wrapper>
        <div className="not-found-screen">
          <div className="not-found-icon">🔍</div>
          <h2 className="not-found-title">{t("account_not_found") || "Аккаунт не найден"}</h2>
          <span className="phone-number">{phoneNumber}</span>
          <p className="not-found-description">{t("not_registered_yet") || "Этот номер ещё не зарегистрирован."}</p>
          <div className="not-found-actions">
            <button className="auth-button" onClick={() => setScreen("chooseRole")}>
              {t("register_now") || "Зарегистрироваться"}
            </button>
            <button className="not-found-back-btn" onClick={() => navigate(`/${lang}/login`)}>
              {t("back_to_login") || "Назад ко входу"}
            </button>
          </div>
        </div>
      </Wrapper>
    );
  }

  // ── Экран: выбор роли ─────────────────────────────────────────────────────
  if (screen === "chooseRole") {
    return (
      <Wrapper>
        <h1 className="auth-title">{t("who_are_you") || "Кто вы?"}</h1>
        <p className="auth-description">{t("choose_your_role") || "Выберите как вы будете использовать ZmanUp"}</p>

        <div className="role-toggle">
          <div
            className={`role-option ${selectedRole === "master" ? "selected" : ""}`}
            onClick={() => setSelectedRole("master")}
          >
            <span className="role-icon">👩‍🎓</span>
            <span className="role-label">{t("i_am_master") || "Я мастер"}</span>
            <span className="role-desc">{t("master_desc") || "Управляю записями и клиентами"}</span>
          </div>
          <div
            className={`role-option ${selectedRole === "user" ? "selected" : ""}`}
            onClick={() => setSelectedRole("user")}
          >
            <span className="role-icon">👤</span>
            <span className="role-label">{t("i_am_user") || "Я клиент"}</span>
            <span className="role-desc">{t("user_desc") || "Записываюсь к мастерам онлайн"}</span>
          </div>
        </div>

        <button className="auth-button" style={{ marginTop: 24 }} disabled={!selectedRole || loading} onClick={handleRoleConfirm}>
          {loading ? t("sending") || "Загрузка..." : t("continue") || "Продолжить"}
        </button>
        <button className="not-found-back-btn" style={{ marginTop: 12 }} onClick={() => setScreen("notFound")}>
          {t("back") || "Назад"}
        </button>
      </Wrapper>
    );
  }

  // ── Экран: регистрация клиента ────────────────────────────────────────────
  if (screen === "registerClient") {
    return (
      <Wrapper>
        <h1 className="auth-title">{t("your_name") || "Как вас зовут?"}</h1>
        <p className="auth-description">{t("enter_name_to_continue") || "Введите имя и фамилию"}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            className="auth-input"
            type="text"
            placeholder={t("enter_your_name") || "Имя"}
            value={clientFirstName}
            onChange={(e) => setClientFirstName(e.target.value)}
          />
          <input
            className="auth-input"
            type="text"
            placeholder={t("enter_your_last_name") || "Фамилия"}
            value={clientLastName}
            onChange={(e) => setClientLastName(e.target.value)}
          />
          <button
            className="auth-button"
            style={{ marginTop: 8 }}
            disabled={!clientFirstName.trim() || loading}
            onClick={handleClientRegister}
          >
            {loading ? t("sending") || "Загрузка..." : t("continue") || "Продолжить"}
          </button>
          <button className="not-found-back-btn" onClick={() => setScreen("chooseRole")}>
            {t("back") || "Назад"}
          </button>
        </div>
      </Wrapper>
    );
  }

  // ── Основной экран: ввод OTP ──────────────────────────────────────────────
  return (
    <Wrapper>
      <h1 className="auth-title">{t("confirm_login")}</h1>
      <p className="auth-description">
        {t("enter_code_from_whatsapp") || "Введите код из WhatsApp"} <br />
        <span className="phone-number">{phoneNumber}</span>
      </p>

      {isTestNumber && (
        <div className="test-mode-badge">
          🧪 {t("test_mode") || "Тестовый режим"} — введи любые 6 цифр
        </div>
      )}

      <form className="auth-form" onSubmit={verifyOtp}>
        <div className="otp-container" dir="ltr" onPaste={handlePaste}>
          {code.map((digit, idx) => (
            <input
              key={idx}
              id={`otp-${idx}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className="otp-input"
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              autoComplete={idx === 0 ? "one-time-code" : "off"}
            />
          ))}
        </div>

        <button className="auth-button" type="submit" disabled={loading || code.join("").length < 6}>
          {loading ? t("verifying") || "Проверка..." : t("verify") || "Подтвердить"}
        </button>

        {!isTestNumber && (
          <div className="resend-code">
            <button type="button" className="resend-button" onClick={resendOtp} disabled={loading}>
              {loading ? t("sending") || "Отправка..." : t("resend_code") || "Отправить повторно"}
            </button>
          </div>
        )}
      </form>
    </Wrapper>
  );
};

export default OtpVerification;